import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { di } from '../../../../../../state/inversify.config';
import { AssessmentCreationViewService } from '../../../assessmentCreationView/assessmentCreation.view.service';
import { ModulesSpecsDomain } from '../modules.domain';
import { Notes } from '../../components/notes/notes.dumb';
import { reverseMonths } from './month.helper';
import { SkipModule } from '../../components/skipModule/skipModule.smart';
import { AssessmentModules } from '../../modules';
import { Instructions } from '../../components/instructions/instructions.dumb';
import { useCustomTranslation } from '../../../../../../translation/customTranslation';
import { ToggleButtonMultipleSelection } from '../../../../../../components/toggleButtonMultipleSelection/toggleButtonMultipleSelection';

export function MonthsInReverse({ domainService }: any) {
  const moduleSpecsDomain = di.get<ModulesSpecsDomain>(ModulesSpecsDomain);
  const { t } = useCustomTranslation('assessment');
  const assessmentCreationViewService = di.get<AssessmentCreationViewService>(
    AssessmentCreationViewService,
  );

  const module = assessmentCreationViewService.useModuleSelector();

  const { monthsInReverse } = module;
  const moduleName: string = 'monthsInReverse';

  const { heading, instructions, note } =
    moduleSpecsDomain.monthsInReverseExamConfig();

  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [score, setScore] = useState<number>(0);
  const [answer, setAnswer] = useState<string>('');
  const [selected, setSelected] = useState(false);

  const saveModule = () => {
    assessmentCreationViewService.dispatchSetAssessmentModule({
      monthsInReverse: {
        monthsRecalled:
          selectedMonths.length === 0
            ? monthsInReverse?.monthsRecalled
            : selectedMonths,

        monthsInReverseScore:
          selectedMonths.length === 0
            ? monthsInReverse?.monthsInReverseScore
            : score,

        monthsInReverseAnswer:
          selectedMonths.length === 0
            ? monthsInReverse?.monthsInReverseAnswer
            : answer,

        monthsInReverseClinicalNotes:
          monthsInReverse?.monthsInReverseClinicalNotes,
        skipModuleFlag: monthsInReverse?.skipModuleFlag,
        skipModuleComments: monthsInReverse?.skipModuleComments,

        NORMATIVE_SCORE: 1,
        MAX_SCORE: 1,
      },
    });
  };

  const checkMonthsOrder = (month: string) => {
    const clickedMonths = reverseMonths.slice(
      0,
      reverseMonths.indexOf(month) + 1,
    );

    setSelectedMonths(clickedMonths);
    setScore(clickedMonths.at(clickedMonths.length - 1) === 'JAN' ? 1 : 0);
    setAnswer(clickedMonths.at(clickedMonths.length - 1) ?? '');

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const saveAnswersResponse = () => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'monthsInReverseScore',
      monthsInReverse?.monthsInReverseScore,
    );

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'monthsInReverseAnswer',
      monthsInReverse?.monthsInReverseAnswer,
    );

    saveModule();
  };

  const setNotesComments = (key: string, value: string) => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'monthsInReverseClinicalNotes',
      value,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  useEffect(() => {
    domainService.dispatchNextEnabledAndValidate(
      monthsInReverse,
      AssessmentModules.MonthsInReverse,
      undefined,
      selectedMonths,
    );
    domainService.dispatchBackEnabled(true);
    saveAnswersResponse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonths, score, monthsInReverse?.skipModuleFlag]);

  useEffect(() => {
    if (monthsInReverse?.skipModuleFlag) {
      assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
        moduleName,
        'monthsInReverseAnswer',
        answer,
      );

      assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
        moduleName,
        'monthsInReverseScore',
        score,
      );

      assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
        moduleName,
        'monthsRecalled',
        selectedMonths,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, score, selectedMonths]);

  useEffect(() => {
    if (JSON.stringify(selectedMonths) === JSON.stringify(reverseMonths)) {
      setSelected(true);
    } else {
      setSelected(false);
    }
  }, [selectedMonths]);

  const skipModuleOnChange = (moduleSkipped: boolean) => {
    if (moduleSkipped) {
      setAnswer('');
      setScore(0);
      setSelectedMonths([]);
    }
  };

  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        {t(heading)}
      </Typography>
      <Instructions instructions={instructions} />
      <Grid container spacing={2}>
        {reverseMonths.map((month) => {
          return (
            <Grid item xs={4} sm={4} md={4} lg={2} key={month}>
              <ToggleButtonMultipleSelection
                questionKey={month}
                responseValue={month}
                label={month}
                onAnswerSelect={checkMonthsOrder}
                data={monthsInReverse?.monthsRecalled}
                selected={selected}
                type="monthsInReverse"
                sx={{ minWidth: 100 }}
                size="small"
              />
            </Grid>
          );
        })}
      </Grid>
      {note && (
        <Notes
          label={t(note.label)}
          keyValue={note.key}
          onSave={setNotesComments}
          data={monthsInReverse?.monthsInReverseClinicalNotes || ''}
          fullWidth
        />
      )}
      <SkipModule
        moduleKeyValue={moduleName}
        skipModuleFlag={monthsInReverse?.skipModuleFlag}
        skipModuleComments={monthsInReverse?.skipModuleComments}
        onChange={skipModuleOnChange}
      />
    </>
  );
}
